'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { OrderMapperUtils } from '@ravago/shared/next-data-access';
import { getOrders } from '@ravago/shared/next-data-access/client';
import { DisplayOrder, MappedOrders, OrderBaseUrls } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { MultiselectOption } from '@ravago/shared/radiance/components/input-field/components/Multiselect/Multiselect';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { TabProps } from '@ravago/shared/radiance/components/tabs/components/Tab/Tab';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { OrderFilterUtils } from '../../../../utils/order/order-filters.utils';
import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { OrderLinesTable } from '../../../shared/order-lines-table/order-lines-table';
import { OrderShipments } from '../../../shared/order/order-shipments';

import { OrderOverviewSearchFilter } from './order-overview-search-filter';

export type Props = {
   globals: RendererGlobals;
   orders: MappedOrders;
   hasCallOffs: boolean;
   isUSBusinessContext: boolean;
   orderBaseUrls: OrderBaseUrls;
};

export const OrderOverview: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      isConsumer,
      actions: { requestCoa, requestDeliveryNote, requestBol, downloadDocument }
   },
   orderBaseUrls,
   orders,
   hasCallOffs,
   isUSBusinessContext
}) => {
   const translate = useTranslations();
   const language = getLanguageCode(locale);
   const [activeTab, setActiveTab] = useState(0);
   const [isFiltered, setIsFiltered] = useState(false);
   const [filteredOrders, setFilteredOrders] = useState<MappedOrders | undefined>();
   const [isClosedLoading, setIsClosedLoading] = useState(true);
   const [closedOrders, setClosedOrders] = useState<DisplayOrder[] | undefined>(undefined);
   const [addressesOptions, setAddressesOptions] = useState<MultiselectOption[]>([]);

   const tabs = useMemo(() => {
      const baseTabs: Array<TabProps> = [
         {
            label: `${
               orders.openOrderLines.length
                  ? `${translate('my-orders.tab.open')} (${
                       isFiltered && filteredOrders ? filteredOrders.openOrderLines.length : orders.openOrderLines.length
                    })`
                  : `${translate('my-orders.tab.open')} (...)`
            }`,
            id: 'open-orders-tab'
         },
         {
            label: `${
               closedOrders !== undefined
                  ? `${translate('my-orders.tab.closed')} (${
                       isFiltered && filteredOrders?.closedOrderLines ? filteredOrders.closedOrderLines.length : closedOrders.length
                    })`
                  : `${translate('my-orders.tab.closed')} (...)`
            }`,
            id: 'closed-orders-tab'
         }
      ];

      return baseTabs;
   }, [orders, language, filteredOrders]);

   useEffect(() => {
      const fetchClosedOrders = async () => {
         const closedOrdersResponse = await getOrders(false);
         let closedOrdersList: DisplayOrder[] = [];

         if (closedOrdersResponse) {
            closedOrdersList = [...closedOrdersList, ...closedOrdersResponse];
         }

         const mappedClosedOrders = OrderMapperUtils.getMappedOrders(closedOrdersList);
         setClosedOrders(mappedClosedOrders.closedOrderLines);
         setIsClosedLoading(false);
      };

      fetchClosedOrders();
   }, []);

   useEffect(() => {
      if (orders) {
         const allOrders = [...orders.openOrderLines];
         if (closedOrders && !isClosedLoading) {
            allOrders.push(...closedOrders);
         }
         setAddressesOptions(OrderFilterUtils.getAddressesOptions(allOrders));
      }
   }, [isClosedLoading, orders, closedOrders]);

   const onSearchOrFilter = (mappedOrders: MappedOrders | undefined, isFilter: boolean) => {
      if (mappedOrders) {
         setFilteredOrders(mappedOrders);
      } else {
         setFilteredOrders(undefined);
      }

      setIsFiltered(isFilter);
   };

   if (orders.openOrderLines.length === 0 && orders.inTransitShipments.length === 0 && orders.transportArrangedShipments.length === 0) {
      return (
         <Box direction="column">
            <EmptyState
               label={translate('my-orders.no-orders-found')}
               image={<OrderNotFoundIllustration />}
               title={translate('shipment.not-found-title')}
            />
            <Divider color="default" orientation="horizontal" />
         </Box>
      );
   }

   return (
      <Box direction="column">
         <Box direction="column">
            <OrderOverviewSearchFilter
               onSearchOrFilter={onSearchOrFilter}
               openOrders={orders}
               closedOrders={closedOrders}
               addressesOptions={addressesOptions}
               hasCallOffs={hasCallOffs}
               disabled={false}
            />

            {(orders.inTransitShipments.length > 0 || orders.transportArrangedShipments.length > 0) && (
               <OrderShipments
                  isConsumer={isConsumer}
                  locale={locale}
                  acceptWebp={acceptWebp}
                  orderDetail={isFiltered && filteredOrders ? filteredOrders : orders}
                  orderBaseUrls={orderBaseUrls}
                  openedFrom="order_overview_shipment"
                  requestBol={requestBol}
                  requestDeliveryNote={requestDeliveryNote}
                  requestCoa={requestCoa}
                  downloadDocument={downloadDocument}
                  isUSBusinessContext={isUSBusinessContext}
               />
            )}

            {isFiltered && filteredOrders?.transportArrangedShipments.length === 0 && filteredOrders?.inTransitShipments.length === 0 && (
               <Typography component="p" variant="body" align="center">
                  {translate('my-orders.order-overview-empty-transport-orders')}
               </Typography>
            )}

            <Box direction="column" spacing={{ top: 'md' }}>
               <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
            </Box>
         </Box>

         {activeTab === 0 && (
            <>
               {((!isFiltered && orders.openOrderLines.length > 0) || (isFiltered && filteredOrders && filteredOrders.openOrderLines.length > 0)) && (
                  <OrderLinesTable
                     orderLines={isFiltered && filteredOrders ? filteredOrders.openOrderLines : orders.openOrderLines}
                     isConsumer={isConsumer}
                     acceptWebp={acceptWebp}
                     locale={locale}
                     itemsPerPage={10}
                     orderBaseUrls={orderBaseUrls}
                     openedFrom="order_overview_order"
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                     isUSBusinessContext={isUSBusinessContext}
                  />
               )}
               {(orders.openOrderLines.length === 0 || (filteredOrders?.openOrderLines.length === 0 && isFiltered)) && (
                  <EmptyState
                     label={translate('my-orders.no-open-orders-intro')}
                     image={<OrderNotFoundIllustration />}
                     title={translate('my-orders.no-open-orders')}
                  />
               )}
            </>
         )}

         {activeTab === 1 && (
            <>
               {isClosedLoading && (
                  <Box direction="column" items="center">
                     <Spinner />
                  </Box>
               )}
               {closedOrders && closedOrders.length > 0 && (
                  <OrderLinesTable
                     orderLines={isFiltered && filteredOrders ? filteredOrders.closedOrderLines : closedOrders}
                     isConsumer={isConsumer}
                     acceptWebp={acceptWebp}
                     locale={locale}
                     orderBaseUrls={orderBaseUrls}
                     itemsPerPage={10}
                     openedFrom="order_overview_order"
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                     isUSBusinessContext={isUSBusinessContext}
                  />
               )}
               {(closedOrders && closedOrders.length === 0) ||
                  (filteredOrders?.closedOrderLines.length === 0 && isFiltered && (
                     <EmptyState
                        label={translate('my-orders.no-open-orders-intro')}
                        image={<OrderNotFoundIllustration />}
                        title={translate('my-orders.no-closed-orders')}
                     />
                  ))}
            </>
         )}
      </Box>
   );
};
